import { useCallback, useEffect, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import agentBillsService from "../../../services/agentBills.service";
import DashboardStatsBar from "../../../components/dashboard/DashboardStatsBar";
import "../../../styles/users.css";
import "../../../styles/confirmation.css";
import "../../../styles/dashboard.css";
import "../../../styles/repoAgentsBills.css";

const MODE_LABELS = {
  CASH: "Cash",
  UPI: "UPI",
  NEFT: "NEFT",
  IMPS: "IMPS",
  RTGS: "RTGS",
  CHEQUE: "Cheque",
  BANK_TRANSFER: "Bank transfer",
};

function money(value) {
  const n = Number(value);
  const amount = Number.isFinite(n) ? n : 0;
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function paymentTypeLabel(type) {
  if (type === "ADVANCE") return "Extra Paid / Advance";
  if (type === "OTHER") return "Other";
  return "Repo";
}

export default function AgentRepoPayment() {
  const { auth } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadMine = useCallback(async () => {
    if (!auth?.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await agentBillsService.getMine(auth.token);
      setDetail(res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your repo payments.");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const totals = detail?.totals || {
    totalPayable: 0,
    paid: 0,
    unpaid: 0,
    extraPaid: 0,
  };
  const vehicles = detail?.vehicles || [];
  const history = detail?.payments || [];

  return (
    <div className="page companies-page cf-page rab-page">
      <div className="users-actions">
        <h2>Repo Payment</h2>
      </div>
      <p className="muted">
        Your seized vehicle payables and payments recorded by admin. View only.
      </p>

      <DashboardStatsBar
        className="dashboard-stats-bar--four"
        items={[
          {
            key: "total",
            label: "Total Payment",
            icon: "💰",
            value: money(totals.totalPayable),
            desc: "Repo + other payable",
          },
          {
            key: "paid",
            label: "Paid",
            icon: "✅",
            value: money(totals.paid),
            desc: "Amount already paid",
          },
          {
            key: "unpaid",
            label: "Unpaid",
            icon: "⏳",
            value: money(totals.unpaid),
            desc: "Remaining balance",
          },
          {
            key: "extra",
            label: "Extra Paid / Advance",
            icon: "➕",
            value: money(totals.extraPaid),
            desc: "Adjusted against future vehicles",
          },
        ]}
      />

      {error ? <p className="error-text">{error}</p> : null}

      {loading ? (
        <p className="cf-excel-loading">Loading your payments…</p>
      ) : (
        <>
          <div className="company-table-wrap cf-excel-sheet table-scroll printable-table-area">
            <table className="users-table excel-grid-table cf-excel-list-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Vehicle Number</th>
                  <th>Customer Name</th>
                  <th>Total Payable</th>
                  <th>Paid</th>
                  <th>Unpaid</th>
                  <th>Extra / Advance</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.length === 0 ? (
                  <tr>
                    <td colSpan={7}>No seized vehicles or payment records found.</td>
                  </tr>
                ) : (
                  vehicles.map((vehicle, index) => (
                    <tr key={vehicle.confirmationId}>
                      <td>{vehicles.length - index}</td>
                      <td>{vehicle.vehicleNumber || "—"}</td>
                      <td>{vehicle.customerName || "—"}</td>
                      <td className="cf-amount-td cf-amount-td--total">
                        {money(vehicle.totalPayable)}
                      </td>
                      <td className="cf-amount-td">{money(vehicle.paid)}</td>
                      <td className="cf-amount-td">{money(vehicle.unpaid)}</td>
                      <td className="cf-amount-td">{money(vehicle.extraApplied)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <section className="rab-history-block">
            <h3 className="rab-pay-card__title">Payment history</h3>
            <div className="company-table-wrap table-scroll">
              <table className="users-table excel-grid-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vehicle</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Mode</th>
                    <th>Reference / Transaction ID</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={7}>No payments recorded yet.</td>
                    </tr>
                  ) : (
                    history.map((payment) => {
                      const vehicle = vehicles.find(
                        (row) =>
                          String(row.confirmationId) === String(payment.confirmationId || "")
                      );
                      return (
                        <tr key={payment._id}>
                          <td>{formatDate(payment.paymentDate)}</td>
                          <td>
                            {vehicle?.vehicleNumber ||
                              (payment.paymentType === "ADVANCE"
                                ? "Extra Paid / Advance"
                                : "—")}
                          </td>
                          <td>{paymentTypeLabel(payment.paymentType)}</td>
                          <td className="cf-amount-td">{money(payment.amount)}</td>
                          <td>{MODE_LABELS[payment.paymentMode] || payment.paymentMode}</td>
                          <td>{payment.reference || "—"}</td>
                          <td>{payment.remarks || "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
